import {
  ServerChat,
  ServerMessage,
  ServerChatWithMessages,
  ChatCreate,
  ChatUpdate,
  UserMessageCreate,
  AIMessageCreate,
  MessageUpdate,
  StreamMessageChunk,
  serverChatToLocal,
  localMessageToServer,
  LocalChat,
  LocalMessage,
  PaginatedChatsResponse
} from '../types/chat';
import { apiGet, apiPost, apiPut, apiDelete, getTokens } from './apiUtils';
import { getCachedApiUrl } from './apiConfig';

/**
 * Конвертирует Python dictionary формат в валидный JSON
 * Правильно обрабатывает вложенный JSON и экранирует кавычки
 */
function convertPythonDictToJson(pythonDict: string): string {
  try {
    // Заменяем Python литералы на JSON
    let result = pythonDict
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');

    // Заменяем одинарные кавычки на двойные, правильно экранируя содержимое
    const tokens = [];
    let inString = false;
    let stringStart = -1;
    let stringChar = '';
    let i = 0;

    while (i < result.length) {
      const char = result[i];
      const prevChar = i > 0 ? result[i - 1] : '';

      // Пропускаем экранированные символы
      if (prevChar === '\\') {
        i++;
        continue;
      }

      if (!inString && char === "'") {
        inString = true;
        stringStart = i;
        stringChar = char;
      } else if (inString && char === stringChar) {
        // Найдена закрывающая кавычка
        const stringContent = result.substring(stringStart + 1, i);
        const escapedContent = stringContent.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        tokens.push({
          start: stringStart,
          end: i + 1,
          replacement: `"${escapedContent}"`
        });
        inString = false;
      }

      i++;
    }

    // Применяем замены в обратном порядке
    for (let j = tokens.length - 1; j >= 0; j--) {
      const token = tokens[j];
      result = result.substring(0, token.start) + token.replacement + result.substring(token.end);
    }

    return result;
  } catch (error) {
    console.error('Ошибка конвертации Python dict:', error);
    return pythonDict;
  }
}

// Утилиты для работы с чатами
export class ChatApiService {
  // Получить все чаты пользователя
  static async getChats(): Promise<ServerChat[]> {
    const response = await apiGet(`${getCachedApiUrl()}/chats/`);
    if (!response.ok) {
      throw new Error(`Ошибка получения чатов: ${response.status}`);
    }
    return response.json();
  }

  // Получить чаты пользователя постранично
  static async getChatsPage(skip = 0, limit = 10): Promise<PaginatedChatsResponse> {
    const response = await apiGet(`${getCachedApiUrl()}/chats/page?skip=${skip}&limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Ошибка получения чатов (пагинация): ${response.status}`);
    }
    return response.json();
  }

  // Получить конкретный чат
  static async getChat(chatId: number): Promise<ServerChat> {
    const response = await apiGet(`${getCachedApiUrl()}/chats/${chatId}`);
    if (!response.ok) {
      throw new Error(`Ошибка получения чата: ${response.status}`);
    }
    return response.json();
  }

  // Получить чат с сообщениями
  static async getChatWithMessages(chatId: number): Promise<ServerChatWithMessages> {
    const response = await apiGet(`${getCachedApiUrl()}/chats/${chatId}/with-messages`);
    if (!response.ok) {
      throw new Error(`Ошибка получения чата с сообщениями: ${response.status}`);
    }
    return response.json();
  }

  // Создать новый чат
  static async createChat(chatData: ChatCreate): Promise<ServerChat> {
    const response = await apiPost(`${getCachedApiUrl()}/chats/`, chatData);
    if (!response.ok) {
      throw new Error(`Ошибка создания чата: ${response.status}`);
    }
    return response.json();
  }

  // Обновить чат
  static async updateChat(chatId: number, updateData: ChatUpdate): Promise<ServerChat> {
    const response = await apiPut(`${getCachedApiUrl()}/chats/${chatId}`, updateData);
    if (!response.ok) {
      throw new Error(`Ошибка обновления чата: ${response.status}`);
    }
    return response.json();
  }

  // Удалить чат
  static async deleteChat(chatId: number): Promise<void> {
    const response = await apiDelete(`${getCachedApiUrl()}/chats/${chatId}`);
    if (!response.ok) {
      throw new Error(`Ошибка удаления чата: ${response.status}`);
    }
  }

  // Получить сообщения чата
  static async getChatMessages(chatId: number): Promise<ServerMessage[]> {
    const response = await apiGet(`${getCachedApiUrl()}/messages/chat/${chatId}`);
    if (!response.ok) {
      throw new Error(`Ошибка получения сообщений: ${response.status}`);
    }
    return response.json();
  }

  // Создать сообщение пользователя
  static async createUserMessage(messageData: UserMessageCreate): Promise<ServerMessage> {
    const response = await apiPost(`${getCachedApiUrl()}/messages/user`, messageData);
    if (!response.ok) {
      throw new Error(`Ошибка создания сообщения пользователя: ${response.status}`);
    }
    return response.json();
  }

  // Создать сообщение AI
  static async createAIMessage(messageData: AIMessageCreate): Promise<ServerMessage> {
    const response = await apiPost(`${getCachedApiUrl()}/messages/ai`, messageData);
    if (!response.ok) {
      throw new Error(`Ошибка создания сообщения AI: ${response.status}`);
    }
    return response.json();
  }

  // Обновить сообщение
  static async updateMessage(messageId: number, updateData: MessageUpdate): Promise<ServerMessage> {
    const response = await apiPut(`${getCachedApiUrl()}/messages/${messageId}`, updateData);
    if (!response.ok) {
      throw new Error(`Ошибка обновления сообщения: ${response.status}`);
    }
    return response.json();
  }

  // Удалить сообщение
  static async deleteMessage(messageId: number): Promise<void> {
    const response = await apiDelete(`${getCachedApiUrl()}/messages/${messageId}`);
    if (!response.ok) {
      throw new Error(`Ошибка удаления сообщения: ${response.status}`);
    }
  }


  // Создать потоковое соединение для получения новых сообщений
  static createChatStream(chatId: number): EventSource {
    // Получаем токен из localStorage
    const { accessToken } = getTokens();
    const tokenParam = accessToken ? `?token=${accessToken}` : '';

    return new EventSource(`${getCachedApiUrl()}/messages/stream/${chatId}${tokenParam}`);
  }

  // Загрузка аудио сообщения пользователя (multipart/form-data)
  static async uploadUserAudio(
    params: {
      chatId: number;
      file: File;
      specialty: string;
      context?: string;
    },
    onProgress?: (percent: number) => void
  ): Promise<Response> {
    const { getTokens, refreshToken } = await import('./apiUtils');
    const { showSessionExpiredDialog } = await import('./sessionUtils');

    const url = `${getCachedApiUrl()}/messages/user/audio`;

    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('chat_id', String(params.chatId));
    formData.append('specialty', params.specialty);
    if (params.context) {
      formData.append('context', params.context);
    }

    // Если офлайн — сохраняем задачу в локальную очередь и возвращаем успешный ответ-эмуляцию
    if (!navigator.onLine) {
      try {
        const queueRaw = localStorage.getItem('audioOutbox') || '[]';
        const queue: any[] = JSON.parse(queueRaw);
        // Сохраняем Blob как data URL для простоты (может быть большим)
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(params.file);
        });
        queue.push({
          chatId: params.chatId,
          fileDataUrl: dataUrl,
          specialty: params.specialty,
          context: params.context || null,
          createdAt: Date.now()
        });
        localStorage.setItem('audioOutbox', JSON.stringify(queue));
        // Возвращаем Response c ok=true, чтобы UI мог показать placeholder
        return new Response(JSON.stringify({ queued: true }), { status: 200, statusText: 'Queued offline' });
      } catch (e) {
        return new Response(JSON.stringify({ error: 'Failed to queue offline audio' }), { status: 500 });
      }
    }

    // Используем XMLHttpRequest для прогресса загрузки с обработкой 401
    return new Promise<Response>(async (resolve, reject) => {
      const performUpload = async (token: string | null, isRetry = false): Promise<void> => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
          if (onProgress && event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            onProgress(percent);
          }
        };

        xhr.onreadystatechange = async () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            // Если получили 401 и это не повторная попытка, пробуем обновить токен
            if (xhr.status === 401 && !isRetry) {
              console.log('apiUtils: uploadUserAudio 401 → try refresh');
              const refreshSuccess = await refreshToken();

              if (refreshSuccess) {
                const { accessToken: newToken } = getTokens();
                // Повторяем загрузку с новым токеном
                await performUpload(newToken, true);
                return;
              } else {
                // Если refresh не удался, показываем диалог истекшего сеанса
                console.log('apiUtils: uploadUserAudio refresh failed');
                showSessionExpiredDialog();
                resolve(new Response(JSON.stringify({ error: 'session_expired' }), {
                  status: 401,
                  statusText: 'Session Expired',
                  headers: { 'Content-Type': 'application/json' }
                }));
                return;
              }
            }

            const responseInit: ResponseInit = {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json' }
            };
            const body = xhr.responseText;
            resolve(new Response(body, responseInit));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Network error during audio upload'));
        };

        xhr.send(formData);
      };

      const { accessToken } = getTokens();
      await performUpload(accessToken);
    });
  }
}

// Утилиты для синхронизации локальных и серверных данных
export class ChatSyncService {
  private static isSyncing = false;
  private static syncPromise: Promise<LocalChat[]> | null = null;
  private static syncCallCount = 0;

  // Синхронизировать все чаты с сервера (метаданные, без сообщений)
  static async syncAllChats(): Promise<LocalChat[]> {
    // Если уже идет синхронизация, возвращаем существующий промис
    if (this.isSyncing && this.syncPromise) {
      return this.syncPromise;
    }

    // Устанавливаем флаг синхронизации
    this.isSyncing = true;

    this.syncPromise = this.performSync();

    try {
      const result = await this.syncPromise;
      return result;
    } catch (error) {
      console.error('❌ Ошибка при синхронизации чатов:', error);
      throw error;
    } finally {
      // Сбрасываем флаг и промис после завершения
      this.isSyncing = false;
      this.syncPromise = null;
    }
  }

  private static async performSync(): Promise<LocalChat[]> {
    try {
      const serverChats = await ChatApiService.getChats();
      const localChats: LocalChat[] = [];

      for (const serverChat of serverChats) {
        // Для общей синхронизации грузим только метаданные чатов, без сообщений.
        // Сообщения будут загружены лениво при открытии конкретного чата.
        const localChat = serverChatToLocal(serverChat, []);
        localChats.push(localChat);
      }

      return localChats;
    } catch (error) {
      console.error('❌ Ошибка синхронизации чатов:', error);
      throw error;
    }
  }

  // Синхронизировать конкретный чат (БЕЗ сообщений - они загружаются через SSE)
  static async syncChat(chatId: number): Promise<LocalChat> {
    try {
      // Получаем только информацию о чате без сообщений
      const serverChats = await ChatApiService.getChats();
      const serverChat = serverChats.find(chat => chat.id === chatId);

      if (!serverChat) {
        throw new Error(`Чат с ID ${chatId} не найден`);
      }

      // Создаем чат БЕЗ сообщений - сообщения будут загружены через SSE поток
      const localChat = serverChatToLocal(serverChat, []);
      console.log(`Синхронизирован чат ${chatId} (сообщения будут загружены через SSE)`);
      return localChat;
    } catch (error) {
      console.error(`Ошибка синхронизации чата ${chatId}:`, error);
      throw error;
    }
  }

  // Отправить локальное сообщение на сервер
  static async syncMessage(localMessage: LocalMessage, chatId: number): Promise<LocalMessage> {
    try {
      const serverMessageData = localMessageToServer(localMessage, chatId);

      let serverMessage: ServerMessage;
      if (localMessage.sender === 'user') {
        serverMessage = await ChatApiService.createUserMessage(serverMessageData as UserMessageCreate);
      } else {
        serverMessage = await ChatApiService.createAIMessage(serverMessageData as AIMessageCreate);
      }

      return {
        ...localMessage,
        serverId: serverMessage.id,
        isDirty: false
      };
    } catch (error) {
      console.error('Ошибка синхронизации сообщения:', error);
      throw error;
    }
  }

  // Создать чат на сервере
  static async createChatOnServer(title: string, firstMessage?: LocalMessage): Promise<LocalChat> {
    try {
      const serverChat = await ChatApiService.createChat({
        title,
        description: ''
      });

      const localChat = serverChatToLocal(serverChat, firstMessage ? [firstMessage] : []);
      // Используем переданное название, а не с сервера
      localChat.title = title;

      // Если есть первое сообщение, отправляем его на сервер
      if (firstMessage) {
        try {
          const syncedMessage = await this.syncMessage(firstMessage, serverChat.id);
          localChat.messages = [syncedMessage];
        } catch (error) {
          console.warn('Не удалось синхронизировать первое сообщение:', error);
          // Оставляем сообщение как несинхронизированное
          localChat.messages = [{ ...firstMessage, isDirty: true }];
        }
      }

      return localChat;
    } catch (error) {
      console.error('Ошибка создания чата на сервере:', error);
      throw error;
    }
  }


  // Удалить чат на сервере
  static async deleteChatOnServer(chatId: number): Promise<void> {
    try {
      await ChatApiService.deleteChat(chatId);
    } catch (error) {
      console.error('Ошибка удаления чата на сервере:', error);
      throw error;
    }
  }

  // Обновить чат на сервере
  static async updateChatOnServer(chatId: number, updateData: { title?: string; description?: string; is_active?: boolean }): Promise<ServerChat> {
    try {
      return await ChatApiService.updateChat(chatId, updateData);
    } catch (error) {
      console.error('Ошибка обновления чата на сервере:', error);
      throw error;
    }
  }
}

// Фоновая отправка аудио из локальной очереди при восстановлении онлайна
export async function flushAudioOutbox(): Promise<void> {
  if (!navigator.onLine) return;
  try {
    const queueRaw = localStorage.getItem('audioOutbox') || '[]';
    const queue: any[] = JSON.parse(queueRaw);
    if (queue.length === 0) return;

    const remaining: any[] = [];
    for (const item of queue) {
      try {
        // Восстанавливаем файл из data URL
        const res = await fetch(item.fileDataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'recording.webm', { type: blob.type || 'audio/webm' });
        const resp = await ChatApiService.uploadUserAudio({
          chatId: item.chatId,
          file,
          specialty: item.specialty,
          context: item.context || undefined,
        });
        if (!resp.ok) {
          remaining.push(item);
        }
      } catch {
        remaining.push(item);
      }
    }
    localStorage.setItem('audioOutbox', JSON.stringify(remaining));
  } catch { }
}

// Утилиты для работы с потоковыми сообщениями
export class ChatStreamService {
  private eventSource: EventSource | null = null;
  private onMessageCallback: ((message: ServerMessage) => void) | null = null;
  private onErrorCallback: ((error: any) => void) | null = null;
  private onHeartbeatCallback: (() => void) | null = null;

  constructor(
    private chatId: number,
    onMessage: (message: ServerMessage) => void,
    onError?: (error: any) => void,
    onHeartbeat?: () => void
  ) {
    this.onMessageCallback = onMessage;
    this.onErrorCallback = onError || null;
    this.onHeartbeatCallback = onHeartbeat || null;
  }

  // Начать прослушивание потока
  start(): void {
    if (this.eventSource) {
      this.stop();
    }

    this.eventSource = ChatApiService.createChatStream(this.chatId);

    this.eventSource.onopen = () => {
      // Поток подключен
    };

    this.eventSource.onmessage = (event) => {
      try {
        // Логируем сырые данные для отладки
        console.log('SSE raw data:', event.data);

        // Проверяем, что данные не пустые
        if (!event.data || event.data.trim() === '') {
          console.warn('Получены пустые данные SSE');
          return;
        }

        // Проверяем, что данные начинаются с '{' или '['
        const trimmedData = event.data.trim();
        if (!trimmedData.startsWith('{') && !trimmedData.startsWith('[')) {
          console.warn('Некорректный формат JSON данных:', trimmedData);
          return;
        }

        let data: StreamMessageChunk;
        try {
          // Сначала пытаемся парсить как есть
          data = JSON.parse(trimmedData);
        } catch (parseError) {
          // Если не получилось, пытаемся конвертировать Python dict в JSON
          try {
            console.log('Пытаемся конвертировать Python dictionary в JSON...');
            const jsonData = convertPythonDictToJson(trimmedData);
            console.log('Конвертированные данные:', jsonData);
            data = JSON.parse(jsonData);
            console.log('Успешно спарсен конвертированный JSON');
          } catch (conversionError) {
            console.error('Ошибка парсинга JSON:', parseError);
            console.error('Ошибка конвертации Python dict:', conversionError);
            console.error('Проблемные данные:', trimmedData);
            console.error('Длина данных:', trimmedData.length);
            console.error('Первые 100 символов:', trimmedData.substring(0, 100));
            return;
          }
        }

        switch (data.event) {
          case 'new_message':
            if (this.onMessageCallback && typeof data.data === 'object') {
              this.onMessageCallback(data.data as ServerMessage);
            }
            break;
          case 'heartbeat':
            if (this.onHeartbeatCallback) {
              this.onHeartbeatCallback();
            }
            break;
          case 'error':
            if (this.onErrorCallback) {
              this.onErrorCallback(data.data);
            }
            break;
          default:
            console.warn('Неизвестное событие SSE:', data.event);
        }
      } catch (error) {
        console.error('Общая ошибка обработки SSE сообщения:', error);
        console.error('Сырые данные события:', event.data);
        console.error('Тип события:', event.type);

        // Не вызываем onErrorCallback для ошибок парсинга JSON,
        // так как это может быть временная проблема с форматом данных
        if (error instanceof SyntaxError && error.message.includes('JSON')) {
          console.warn('Пропускаем некорректное JSON сообщение, поток продолжает работать');
        } else if (this.onErrorCallback) {
          this.onErrorCallback(error);
        }
      }
    };

    this.eventSource.onerror = (error) => {
      console.error(`Ошибка потока чата ${this.chatId}:`, error);
      if (this.onErrorCallback) {
        this.onErrorCallback(error);
      }
    };
  }

  // Остановить прослушивание потока
  stop(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  // Проверить, активен ли поток
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }
}
